import { isEmpty } from 'lodash'
import { createContext, FC, ReactElement, useCallback, useContext, useState } from 'react'

type Accordion = {
  [id: string]: boolean;
}

interface ISideNavigationContext {
  bind: (id: string, isDefaultOpened?: boolean) => void;
  unbind: (id: string) => void;
  toggle: (id: string) => void;
  setVisibility: (isVisible: boolean) => void;
  accordion: Accordion;
  isVisible: boolean;
}

const SideNavigationContext = createContext<ISideNavigationContext>({} as ISideNavigationContext)

export const useSideNavigation = (): ISideNavigationContext => {
  const context = useContext(SideNavigationContext)
  if (!context) {
    throw new Error('useSideNavigation must be used within an SideNavigationProvider')
  }

  return context
}

export const SideNavigationProvider: FC = ({ children }): ReactElement => {
  const [accordion, setAccordion] = useState< Accordion>({})
  const [isVisible, setVisibility] = useState(false)

  const bind = useCallback((id: string) => {
    setAccordion(accordion => ({ ...accordion, [id]: isEmpty(accordion) }))
  }, [setAccordion])

  const unbind = useCallback((id: string) => {
    setAccordion(accordion => {
      const accordion2 = { ...accordion }

      delete accordion2[id]

      return accordion2
    })
  }, [setAccordion])

  const toggle = useCallback((id: string) => {
    setAccordion(accordion => {
      return Object.keys(accordion).reduce((acc, key) => {
        if (key === id) return { ...acc, [key]: true }
        return { ...acc, [key]: false }
      }, {})
    })
  }, [setAccordion])

  return (
    <SideNavigationContext.Provider
      value={{
        bind,
        unbind,
        toggle,
        setVisibility,
        isVisible,
        accordion
      }}
    >
      {children}
    </SideNavigationContext.Provider>
  )
}
